import React from "react";
import { IEditBillingForm } from "../../../../../utils/interfaces/Accounting/Recap/IEditBillingForm";

interface PaymentMethod {
    code: string;
    official_code: string;
    label?: string;
}

interface BillingEditFormProps {
    form: IEditBillingForm;
    setForm: React.Dispatch<React.SetStateAction<IEditBillingForm>>;
    error: string | null;
    onSaveClick: React.MouseEventHandler<HTMLButtonElement>;
    onCancelClick: React.MouseEventHandler<HTMLButtonElement>;
    paymentMethods: PaymentMethod[] | undefined;
    isPmLoading: boolean;
    isPmError: boolean;
    isSaving: boolean;
}

const BillingEditForm: React.FC<BillingEditFormProps> = ({
    form,
    setForm,
    error,
    onSaveClick,
    onCancelClick,
    paymentMethods,
    isPmLoading,
    isPmError,
    isSaving,
}) => {
    return (
        <div className="border rounded p-3 bg-white">
            <h4 className="font-semibold mb-2">Modifica dati fatturazione</h4>
            {error && <div className="mb-2 text-red-600 text-sm">{error}</div>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                    <label className="block text-xs text-gray-600 mb-1">Ragione Sociale</label>
                    <input
                        type="text"
                        className="w-full border px-3 py-2 rounded"
                        value={form.name || ""}
                        onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                    />
                </div>
                <div>
                    <label className="block text-xs text-gray-600 mb-1">P.IVA</label>
                    <input
                        type="text"
                        className="w-full border px-3 py-2 rounded"
                        value={form.vat || ""}
                        onChange={(e) => setForm((prev) => ({ ...prev, vat: e.target.value || null }))}
                    />
                </div>
                <div>
                    <label className="block text-xs text-gray-600 mb-1">Cisco ID</label>
                    <input
                        type="number"
                        className="w-full border px-3 py-2 rounded"
                        value={form.cisco_id}
                        onChange={(e) => setForm((prev) => ({ ...prev, cisco_id: e.target.value}))}
                    />
                </div>
                <div>
                    <label className="block text-xs text-gray-600 mb-1">Codice Fiscale</label>
                    <input
                        type="text"
                        className="w-full border px-3 py-2 rounded"
                        value={form.tax_id || ""}
                        onChange={(e) => setForm((prev) => ({ ...prev, tax_id: e.target.value || null }))}
                    />
                </div>
                <div>
                    <label className="block text-xs text-gray-600 mb-1">Indirizzo</label>
                    <input
                        type="text"
                        className="w-full border px-3 py-2 rounded"
                        value={form.address || ""}
                        onChange={(e) => setForm((prev) => ({ ...prev, address: e.target.value || null }))}
                    />
                </div>
                <div>
                    <label className="block text-xs text-gray-600 mb-1">Città</label>
                    <input
                        type="text"
                        className="w-full border px-3 py-2 rounded"
                        value={form.city || ""}
                        onChange={(e) => setForm((prev) => ({ ...prev, city: e.target.value || null }))}
                    />
                </div>
                <div>
                    <label className="block text-xs text-gray-600 mb-1">CAP</label>
                    <input
                        type="text"
                        className="w-full border px-3 py-2 rounded"
                        value={form.zip || ""}
                        onChange={(e) => setForm((prev) => ({ ...prev, zip: e.target.value || null }))}
                    />
                </div>
                <div>
                    <label className="block text-xs text-gray-600 mb-1">Provincia</label>
                    <input
                        type="text"
                        className="w-full border px-3 py-2 rounded"
                        value={form.province || ""}
                        onChange={(e) => setForm((prev) => ({ ...prev, province: e.target.value || null }))}
                    />
                </div>
                <div>
                    <label className="block text-xs text-gray-600 mb-1">Codice pagamento</label>
                    <select
                        className="w-full border px-3 py-2 rounded"
                        value={form.payment_code || ""}
                        onChange={(e) => setForm((prev) => ({ ...prev, payment_code: e.target.value }))}
                    >
                        <option value="">{isPmLoading ? "Caricamento..." : "Seleziona un codice pagamento"}</option>
                        {paymentMethods && paymentMethods.map((pm) => (
                            <option key={pm.code} value={pm.code}>
                                {pm.official_code}
                                {pm.label ? ` - ${pm.label} - ` : ""}
                                {pm.code}
                            </option>
                        ))}
                    </select>
                    {isPmError && (
                        <p className="text-xs text-red-500 mt-1">Errore nel caricamento dei metodi di pagamento</p>
                    )}
                </div>
                <div>
                    <label className="block text-xs text-gray-600 mb-1">Metodo pagamento</label>
                    <input
                        type="text"
                        className="w-full border px-3 py-2 rounded"
                        value={form.payment_method || ""}
                        onChange={(e) => setForm((prev) => ({ ...prev, payment_method: e.target.value }))}
                    />
                </div>
                <div>
                    <label className="block text-xs text-gray-600 mb-1">Email Univoca</label>
                    <input
                        type="email"
                        className="w-full border px-3 py-2 rounded"
                        placeholder="es. fatture+123@dominio.it"
                        value={form.unique_email || ""}
                        onChange={(e) => setForm((prev) => ({ ...prev, unique_email: e.target.value || null }))}
                    />
                </div>
            </div>
            <div className="mt-3 flex gap-2">
                <button
                    type="button"
                    className="px-3 py-1 rounded border bg-mcia_orange text-white disabled:opacity-60 flex items-center gap-2"
                    disabled={isSaving}
                    onClick={onSaveClick}
                >
                    {isSaving ? (
                        <>
                            <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span>Salvataggio...</span>
                        </>
                    ) : (
                        "Salva"
                    )}
                </button>
                <button type="button" className="px-3 py-1 rounded border bg-gray-100 hover:bg-gray-200" onClick={onCancelClick}>
                    Annulla
                </button>
            </div>
        </div>
    );
};

export default BillingEditForm;
